import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  X, CheckCircle, Camera, Upload, Star, PenTool, AlertTriangle,
  User, Clock, MapPin, FileText, Image, MessageSquare, ThumbsUp
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with credentials for authenticated requests
const authAxios = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

const JobCompletionModal = ({ 
  booking, 
  onClose, 
  onSubmit, 
  currentLocation 
}) => {
  const [step, setStep] = useState(1); // 1: Checklist, 2: Photos, 3: Rating & Notes, 4: Signatures
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Checklist state
  const [checklist, setChecklist] = useState([]);
  const [serviceType, setServiceType] = useState('');
  
  // Photos state
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Rating and Notes state
  const [cooperationRating, setCooperationRating] = useState(0);
  const [cooperationNotes, setCooperationNotes] = useState('');
  const [workerNotes, setWorkerNotes] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  
  // Signature state
  const [workerSignatureName, setWorkerSignatureName] = useState('');
  const [customerSignatureName, setCustomerSignatureName] = useState('');
  const [customerUnavailable, setCustomerUnavailable] = useState(false);
  const [customerUnavailableReason, setCustomerUnavailableReason] = useState('');
  
  // Canvas refs for signatures
  const workerCanvasRef = useRef(null);
  const customerCanvasRef = useRef(null);
  const [isDrawingWorker, setIsDrawingWorker] = useState(false);
  const [isDrawingCustomer, setIsDrawingCustomer] = useState(false);
  const [workerSignatureData, setWorkerSignatureData] = useState('');
  const [customerSignatureData, setCustomerSignatureData] = useState('');

  useEffect(() => {
    fetchChecklist();
  }, [booking.id]);

  const fetchChecklist = async () => {
    try {
      const res = await authAxios.get(`/api/completion-forms/checklist/${booking.id}`);
      setChecklist(res.data.checklist || []);
      setServiceType(res.data.service_type || booking.service_type || '');
    } catch (err) {
      console.error('Failed to fetch checklist:', err);
      // Use booking's service_type as fallback
      setServiceType(booking.service_type || '');
      // Set default checklist if API fails
      setChecklist([
        { item_id: 'service_completed', label: 'Service completed as requested', completed: false },
        { item_id: 'customer_satisfied', label: 'Customer satisfaction verified', completed: false },
        { item_id: 'area_cleaned_up', label: 'Work area cleaned up', completed: false }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistToggle = (index) => {
    const updated = [...checklist];
    updated[index].completed = !updated[index].completed;
    setChecklist(updated);
  };

  const handleChecklistNote = (index, note) => {
    const updated = [...checklist];
    updated[index].notes = note;
    setChecklist(updated);
  };

  const handlePhotoUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhoto(true);

    try {
      for (const file of files) {
        // Upload to completion forms storage first
        const formData = new FormData();
        formData.append('file', file);
        formData.append('photo_type', type);

        const res = await authAxios.post(`/api/completion-forms/upload-photo`, formData, {
          headers: { 
            'Content-Type': 'multipart/form-data'
          }
        });

        if (type === 'before') {
          setBeforePhotos(prev => [...prev, res.data.url]);
        } else {
          setAfterPhotos(prev => [...prev, res.data.url]);
        }

        // Also submit to Gallery API for owner approval (cleaning services only)
        if (serviceType === 'cleaning' && booking?.id) {
          try {
            const galleryFormData = new FormData();
            galleryFormData.append('booking_id', booking.id);
            galleryFormData.append('photo_type', type);
            galleryFormData.append('photo', file);
            galleryFormData.append('caption', `${type === 'before' ? 'Before' : 'After'} cleaning photo`);

            await authAxios.post(`/api/gallery/upload`, galleryFormData, {
              headers: { 
                'Content-Type': 'multipart/form-data'
              }
            });
          } catch (galleryErr) {
            // Gallery upload failure shouldn't block the completion form
          }
        }
      }
      toast.success(`${type === 'before' ? 'Before' : 'After'} photo(s) uploaded${serviceType === 'cleaning' ? ' - Submitted for gallery review' : ''}`);
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (type, index) => {
    if (type === 'before') {
      setBeforePhotos(prev => prev.filter((_, i) => i !== index));
    } else {
      setAfterPhotos(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Canvas drawing functions
  const initCanvas = (canvasRef) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (step === 4) {
      initCanvas(workerCanvasRef);
      if (!customerUnavailable) {
        initCanvas(customerCanvasRef);
      }
    }
  }, [step, customerUnavailable]);

  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e, canvasRef, setIsDrawing) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e, canvas);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e, canvasRef, isDrawing) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e, canvas);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (canvasRef, setIsDrawing, setSignatureData) => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = (canvasRef, setSignatureData) => {
    initCanvas(canvasRef);
    setSignatureData('');
  };

  const handleSubmit = async () => {
    // Validation
    if (!workerSignatureName.trim()) {
      toast.error('Please enter your name for the signature');
      return;
    }
    if (!workerSignatureData) {
      toast.error('Please sign in the signature box');
      return;
    }
    if (customerUnavailable && !customerUnavailableReason.trim()) {
      toast.error('Please provide a reason why customer is unavailable');
      return;
    }

    setSubmitting(true);

    try {
      // Submit completion form
      await authAxios.post(`/api/completion-forms`, {
        booking_id: booking.id,
        checklist_items: checklist,
        worker_notes: workerNotes,
        additional_comments: additionalComments,
        before_photos: beforePhotos,
        after_photos: afterPhotos,
        customer_cooperation_rating: cooperationRating || null,
        cooperation_notes: cooperationNotes,
        worker_signature_name: workerSignatureName,
        worker_signature_data: workerSignatureData,
        customer_signature_name: customerUnavailable ? null : customerSignatureName,
        customer_signature_data: customerUnavailable ? null : customerSignatureData,
        customer_unavailable: customerUnavailable,
        customer_unavailable_reason: customerUnavailable ? customerUnavailableReason : null
      });

      toast.success('Job completion form submitted!');
      onSubmit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit completion form');
      setSubmitting(false);
    }
  };

  const completedCount = checklist.filter(item => item.completed).length;
  const totalCount = checklist.length;
  
  // Private Concierge and Social Engagement services don't need photos - only 3 steps
  // Cleaning services have photos - 4 steps
  const isPersonalService = serviceType === 'private_concierge' || serviceType === 'social_engagement';
  const totalSteps = isPersonalService ? 3 : 4;
  
  // Map logical steps based on service type
  // Personal Services: 1=Checklist, 2=Rating, 3=Signatures
  // Cleaning: 1=Checklist, 2=Photos, 3=Rating, 4=Signatures
  const getStepLabel = (stepNum) => {
    if (isPersonalService) {
      return stepNum === 1 ? 'Checklist' : stepNum === 2 ? 'Rating' : 'Signatures';
    }
    return stepNum === 1 ? 'Checklist' : stepNum === 2 ? 'Photos' : stepNum === 3 ? 'Rating' : 'Signatures';
  };
  
  // Get actual content step (for Personal Services, step 2 shows Rating, step 3 shows Signatures)
  const getContentStep = () => {
    if (isPersonalService) {
      // Personal Services: skip photos step
      if (step === 1) return 'checklist';
      if (step === 2) return 'rating';
      if (step === 3) return 'signatures';
    } else {
      // Cleaning: all 4 steps
      if (step === 1) return 'checklist';
      if (step === 2) return 'photos';
      if (step === 3) return 'rating';
      if (step === 4) return 'signatures';
    }
    return 'checklist';
  };
  
  const contentStep = getContentStep();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-[var(--background-paper)] p-8 rounded-2xl">
          <div className="animate-pulse text-[var(--primary)]">Loading checklist...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[var(--background-paper)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-[var(--primary)] to-[var(--accent)] text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Job Completion Form</h2>
              <p className="text-sm opacity-90">{booking.service_name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--background-paper)]/20 rounded-full transition">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition ${
                  step === s ? 'bg-[var(--background-paper)] text-[var(--primary)]' : 
                  step > s ? 'bg-[var(--background-paper)]/30 text-white' : 'bg-[var(--background-paper)]/10 text-white/50'
                }`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < totalSteps && <div className={`w-8 h-0.5 ${step > s ? 'bg-[var(--background-paper)]/50' : 'bg-[var(--background-paper)]/20'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs mt-2 opacity-75">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
              <span key={s}>{getStepLabel(s)}</span>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Checklist Step */}
          {contentStep === 'checklist' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[var(--primary)]" />
                  {serviceType === 'private_concierge' ? 'Private Concierge Checklist' : 
                   serviceType === 'social_engagement' ? 'Social Engagement Checklist' :
                   serviceType === 'cleaning' ? 'Cleaning Checklist' : 'Service Checklist'}
                </h3>
                <span className="text-sm text-[var(--text-muted)]">
                  {completedCount}/{totalCount} completed
                </span>
              </div>

              <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                <p className="text-sm text-[var(--text-muted)] mb-4">
                  Check off each task as you complete it. Add notes if needed.
                </p>
                
                <div className="space-y-3">
                  {checklist.map((item, index) => (
                    <div key={item.item_id} className="bg-[var(--background-paper)] p-3 rounded-lg border">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={() => handleChecklistToggle(index)}
                          className="mt-0.5"
                          data-testid={`checklist-${item.item_id}`}
                        />
                        <div className="flex-1">
                          <span className={`font-medium ${item.completed ? 'text-[var(--success)] line-through' : ''}`}>
                            {item.label}
                          </span>
                          <Input
                            placeholder="Add notes (optional)"
                            value={item.notes || ''}
                            onChange={(e) => handleChecklistNote(index, e.target.value)}
                            className="mt-2 text-sm"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Photos Step - Only for Cleaning services */}
          {contentStep === 'photos' && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Camera className="w-5 h-5 text-[var(--primary)]" />
                Before & After Photos
              </h3>

              {/* Gallery Notice */}
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-3 rounded-lg flex items-start gap-2">
                <Image className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Gallery Submission</p>
                  <p className="text-xs text-[#D4AF37]">
                    Photos will be submitted for owner review and may be featured in our public gallery to showcase your great work!
                  </p>
                </div>
              </div>

              {/* Before Photos */}
              <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                <Label className="font-medium mb-2 block">Before Photos</Label>
                <p className="text-sm text-[var(--text-muted)] mb-3">
                  Document the initial condition before your work
                </p>
                
                <div className="flex flex-wrap gap-3 mb-3">
                  {beforePhotos.map((url, index) => (
                    <div key={`before-photo-${index}-${url.substring(url.length - 10)}`} className="relative w-24 h-24">
                      <img src={url} alt={`Before ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                      <button 
                        onClick={() => removePhoto('before', index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 border-2 border-dashed border-[var(--border-primary)] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-rose-50 transition">
                    <Upload className="w-6 h-6 text-[var(--primary)]" />
                    <span className="text-xs mt-1">Upload</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      className="hidden" 
                      onChange={(e) => handlePhotoUpload(e, 'before')}
                      disabled={uploadingPhoto}
                    />
                  </label>
                </div>
              </div>

              {/* After Photos */}
              <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                <Label className="font-medium mb-2 block">After Photos</Label>
                <p className="text-sm text-[var(--text-muted)] mb-3">
                  Document the completed work
                </p>
                
                <div className="flex flex-wrap gap-3 mb-3">
                  {afterPhotos.map((url, index) => (
                    <div key={`after-photo-${index}-${url.substring(url.length - 10)}`} className="relative w-24 h-24">
                      <img src={url} alt={`After ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                      <button 
                        onClick={() => removePhoto('after', index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 border-2 border-dashed border-[var(--border-primary)] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-rose-50 transition">
                    <Upload className="w-6 h-6 text-[var(--primary)]" />
                    <span className="text-xs mt-1">Upload</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      className="hidden" 
                      onChange={(e) => handlePhotoUpload(e, 'after')}
                      disabled={uploadingPhoto}
                    />
                  </label>
                </div>
              </div>

              {uploadingPhoto && (
                <div className="text-center text-[var(--primary)]">
                  <div className="animate-spin inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full"></div>
                  <p className="text-sm mt-2">Uploading...</p>
                </div>
              )}
            </div>
          )}

          {/* Rating & Notes Step */}
          {contentStep === 'rating' && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-[var(--primary)]" />
                Rating & Notes
              </h3>

              {/* Customer Cooperation Rating */}
              <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                <Label className="font-medium mb-2 block">Customer Cooperation Rating</Label>
                <p className="text-sm text-[var(--text-muted)] mb-3">
                  How cooperative was the customer during the service?
                </p>
                
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setCooperationRating(rating)}
                      className={`p-2 rounded-lg transition ${
                        cooperationRating >= rating 
                          ? 'text-yellow-500' 
                          : 'text-gray-300 hover:text-yellow-300'
                      }`}
                      data-testid={`rating-star-${rating}`}
                    >
                      <Star className="w-8 h-8" fill={cooperationRating >= rating ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
                
                {cooperationRating > 0 && (
                  <p className="text-sm mt-2 text-[var(--text-muted)]">
                    {cooperationRating === 5 ? 'Excellent - Very cooperative' :
                     cooperationRating === 4 ? 'Good - Generally cooperative' :
                     cooperationRating === 3 ? 'Average - Some cooperation' :
                     cooperationRating === 2 ? 'Below Average - Limited cooperation' :
                     'Poor - Not cooperative'}
                  </p>
                )}

                <Input
                  placeholder="Additional notes about cooperation (optional)"
                  value={cooperationNotes}
                  onChange={(e) => setCooperationNotes(e.target.value)}
                  className="mt-3"
                />
              </div>

              {/* Worker Notes */}
              <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                <Label className="font-medium mb-2 block flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Worker Notes
                </Label>
                <textarea
                  placeholder="Any notes about the job, issues encountered, or things to remember for next time..."
                  value={workerNotes}
                  onChange={(e) => setWorkerNotes(e.target.value)}
                  className="w-full p-3 border rounded-lg min-h-[100px] resize-none"
                  data-testid="worker-notes"
                />
              </div>

              {/* Additional Comments */}
              <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                <Label className="font-medium mb-2 block">Additional Comments</Label>
                <textarea
                  placeholder="Any other comments or feedback..."
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                  className="w-full p-3 border rounded-lg min-h-[80px] resize-none"
                  data-testid="additional-comments"
                />
              </div>
            </div>
          )}

          {/* Signatures Step */}
          {contentStep === 'signatures' && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <PenTool className="w-5 h-5 text-[var(--primary)]" />
                Signatures
              </h3>

              {/* Worker Signature (Required) */}
              <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                <Label className="font-medium mb-2 block">Worker Signature *</Label>
                <Input
                  placeholder="Enter your full name"
                  value={workerSignatureName}
                  onChange={(e) => setWorkerSignatureName(e.target.value)}
                  className="mb-3"
                  data-testid="worker-signature-name"
                />
                
                <div className="border-2 border-dashed border-[var(--border-primary)] rounded-lg bg-[var(--background-paper)]">
                  <canvas
                    ref={workerCanvasRef}
                    width={400}
                    height={150}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={(e) => startDrawing(e, workerCanvasRef, setIsDrawingWorker)}
                    onMouseMove={(e) => draw(e, workerCanvasRef, isDrawingWorker)}
                    onMouseUp={() => stopDrawing(workerCanvasRef, setIsDrawingWorker, setWorkerSignatureData)}
                    onMouseLeave={() => stopDrawing(workerCanvasRef, setIsDrawingWorker, setWorkerSignatureData)}
                    onTouchStart={(e) => startDrawing(e, workerCanvasRef, setIsDrawingWorker)}
                    onTouchMove={(e) => draw(e, workerCanvasRef, isDrawingWorker)}
                    onTouchEnd={() => stopDrawing(workerCanvasRef, setIsDrawingWorker, setWorkerSignatureData)}
                    data-testid="worker-signature-canvas"
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-[var(--text-muted)]">Sign above</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => clearCanvas(workerCanvasRef, setWorkerSignatureData)}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* Customer Unavailable Option */}
              <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 p-4 rounded-xl">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={customerUnavailable}
                    onCheckedChange={(checked) => setCustomerUnavailable(checked)}
                    className="mt-0.5"
                    data-testid="customer-unavailable-checkbox"
                  />
                  <div>
                    <span className="font-medium">Customer not available to sign</span>
                    <p className="text-sm text-amber-700">
                      Check this if the customer/patient is not available to provide their signature.
                    </p>
                  </div>
                </label>
                
                {customerUnavailable && (
                  <Input
                    placeholder="Reason (e.g., Customer was asleep, No one home...)"
                    value={customerUnavailableReason}
                    onChange={(e) => setCustomerUnavailableReason(e.target.value)}
                    className="mt-3"
                    data-testid="customer-unavailable-reason"
                  />
                )}
              </div>

              {/* Customer Signature (Optional) */}
              {!customerUnavailable && (
                <div className="bg-[var(--background-subtle)] p-4 rounded-xl">
                  <Label className="font-medium mb-2 block">Customer Signature (Optional)</Label>
                  <Input
                    placeholder="Enter customer's full name"
                    value={customerSignatureName}
                    onChange={(e) => setCustomerSignatureName(e.target.value)}
                    className="mb-3"
                    data-testid="customer-signature-name"
                  />
                  
                  <div className="border-2 border-dashed border-[var(--border-primary)] rounded-lg bg-[var(--background-paper)]">
                    <canvas
                      ref={customerCanvasRef}
                      width={400}
                      height={150}
                      className="w-full cursor-crosshair touch-none"
                      onMouseDown={(e) => startDrawing(e, customerCanvasRef, setIsDrawingCustomer)}
                      onMouseMove={(e) => draw(e, customerCanvasRef, isDrawingCustomer)}
                      onMouseUp={() => stopDrawing(customerCanvasRef, setIsDrawingCustomer, setCustomerSignatureData)}
                      onMouseLeave={() => stopDrawing(customerCanvasRef, setIsDrawingCustomer, setCustomerSignatureData)}
                      onTouchStart={(e) => startDrawing(e, customerCanvasRef, setIsDrawingCustomer)}
                      onTouchMove={(e) => draw(e, customerCanvasRef, isDrawingCustomer)}
                      onTouchEnd={() => stopDrawing(customerCanvasRef, setIsDrawingCustomer, setCustomerSignatureData)}
                      data-testid="customer-signature-canvas"
                    />
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-[var(--text-muted)]">Customer signs above</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => clearCanvas(customerCanvasRef, setCustomerSignatureData)}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-[var(--success)]/10 border border-[var(--success)]/30 p-4 rounded-xl">
                <h4 className="font-medium text-[var(--success)] mb-2">Completion Summary</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>Checklist: {completedCount}/{totalCount} items completed</li>
                  <li>Before Photos: {beforePhotos.length}</li>
                  <li>After Photos: {afterPhotos.length}</li>
                  <li>Cooperation Rating: {cooperationRating > 0 ? `${cooperationRating}/5 stars` : 'Not rated'}</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-[var(--background-subtle)] flex justify-between">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          
          {step < totalSteps ? (
            <Button onClick={() => setStep(step + 1)} className="btn-primary">
              Next
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !workerSignatureName.trim() || !workerSignatureData}
              className="btn-primary"
              data-testid="submit-completion-form"
            >
              {submitting ? 'Submitting...' : 'Complete & Clock Out'}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default JobCompletionModal;
